import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, FileText, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { cn, formatRelativeDate } from '@/lib/utils';

interface ContractItem {
  contractId: string;
  title: string;
  status: string;
  contractType?: string;
  createdAt: string;
}

type StatusFilter = 'all' | 'analyzing' | 'analyzed' | 'archived';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'analyzing', label: 'Analisando' },
  { value: 'analyzed', label: 'Analisados' },
  { value: 'archived', label: 'Arquivados' },
];

export function ContratosLista() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => api.get<{ items: ContractItem[] }>('/contracts'),
  });

  const filtered = useMemo(() => {
    let arr = data?.items ?? [];
    if (filter !== 'all') {
      arr = arr.filter((c) => {
        if (filter === 'analyzing') return c.status === 'analyzing' || c.status === 'uploaded';
        return c.status === filter;
      });
    }
    const n = search
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
    if (n) {
      arr = arr.filter((c) => {
        const hay = `${c.title} ${c.contractType ?? ''}`
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '');
        return hay.includes(n);
      });
    }
    return arr;
  }, [data, filter, search]);

  // Group by month for the "histórico jurídico" feel.
  const grouped = useMemo(() => {
    const m = new Map<string, ContractItem[]>();
    for (const c of filtered) {
      const d = new Date(c.createdAt);
      const key = d.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    }
    return Array.from(m.entries());
  }, [filtered]);

  return (
    <AppShell role="users">
      <div className="max-w-5xl">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-display-lg">Histórico de contratos</h1>
            <p className="text-muted-foreground mt-1">
              Todos os contratos que você já subiu, com suas análises IA e versões revisadas.
              Backup com retenção de 2 anos.
            </p>
          </div>
          <Button asChild variant="accent" className="gap-2">
            <Link to="/app/contratos/novo">
              <Plus className="h-4 w-4" />
              Novo
            </Link>
          </Button>
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar por título ou tipo…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-1.5">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setFilter(o.value)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs transition-colors',
                  filter === o.value
                    ? 'border-accent bg-accent/15 text-foreground font-medium'
                    : 'border-border text-muted-foreground hover:border-accent/40',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          {isLoading
            ? 'Carregando…'
            : `${filtered.length} ${filtered.length === 1 ? 'contrato' : 'contratos'}`}
        </p>

        <div className="mt-6 space-y-8">
          {!isLoading && filtered.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-muted-foreground text-sm">
                {search || filter !== 'all'
                  ? 'Nenhum contrato bate com os filtros.'
                  : 'Você ainda não subiu nenhum contrato.'}
              </CardContent>
            </Card>
          )}
          {grouped.map(([monthLabel, list]) => (
            <div key={monthLabel}>
              <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                {monthLabel}
              </h2>
              <div className="space-y-2">
                {list.map((c) => (
                  <Link key={c.contractId} to={`/app/contratos/${c.contractId}`} className="block focus-ring">
                    <Card className="hover:border-accent/40 transition-colors">
                      <CardContent className="py-4 flex items-center gap-4">
                        <FileText className="h-5 w-5 text-muted-foreground flex-none" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{c.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.contractType ?? 'Contrato'} · {formatRelativeDate(c.createdAt)}
                          </p>
                        </div>
                        <Badge variant={statusVariant(c.status)}>{statusLabel(c.status)}</Badge>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function statusLabel(s: string): string {
  return (
    {
      uploaded: 'Aguardando análise',
      analyzing: 'Analisando',
      analyzed: 'Analisado',
      in_review: 'Em revisão',
      reviewed: 'Revisado',
      archived: 'Arquivado',
    }[s] ?? s
  );
}

function statusVariant(s: string): 'default' | 'success' | 'warning' | 'muted' {
  if (s === 'analyzed' || s === 'reviewed') return 'success';
  if (s === 'analyzing' || s === 'in_review' || s === 'uploaded') return 'warning';
  if (s === 'archived') return 'muted';
  return 'default';
}
